import React, { useState, useEffect } from "react";
import axios from "axios";
import Constant from "../Constant";
import EditSubscriptionModal from "./EditSubscriptionModal.js";
import DeleteConfirmationModal from "../Common/DeleteConfirmationModal";
import Notification from "../Common/Notification";

const Subscription = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [subsPerPage, setSubsPerPage] = useState(10);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [totalPages, setTotalPages] = useState(1);

  // Show notification helper
  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 3000);
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [currentPage, subsPerPage]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${Constant.apiBasePath}subscriptions/all`
      );
      setSubscriptions(response.data);
      console.log("Subscription", response.data);
      setTotalPages(Math.ceil(response.data.length / subsPerPage));
      setLoading(false);
    } catch (error) {
      showNotification("Failed to fetch subscriptions", "error");
      console.error("Error fetching subscriptions:", error);
      setLoading(false);
    }
  };

  // Get current subscriptions for pagination
  const indexOfLastSub = currentPage * subsPerPage;
  const indexOfFirstSub = indexOfLastSub - subsPerPage;
  const currentSubs = subscriptions.slice(indexOfFirstSub, indexOfLastSub);

  // Change page
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  // Handle edit subscription
  const handleEditClick = (subscription) => {
    setSelectedSub(subscription);
    setShowEditModal(true);
  };

  // Handle delete subscription
  const handleDeleteClick = (subscription) => {
    setSelectedSub(subscription);
    setShowDeleteModal(true);
  };

  // Delete subscription
  const confirmDelete = async () => {
    try {
      await axios.delete(
        `${Constant.apiBasePath}subscriptions/${selectedSub._id}`
      );
      showNotification("Subscription deleted successfully");
      setShowDeleteModal(false);
      fetchSubscriptions();
    } catch (error) {
      if (error.response && error.response.status === 400) {
        showNotification(error.response.data.message, "error");
      } else {
        showNotification("Failed to delete subscription", "error");
      }
      console.error("Error deleting subscription:", error);
    }
  };

  // After edit refresh subscriptions
  const onSubscriptionUpdated = () => {
    fetchSubscriptions();
  };

  const handleLimitChange = (e) => {
    setSubsPerPage(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page when changing limit
  };

  // Format date function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="content-page">
      <div className="content">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {notification.show && (
                  <Notification
                    message={notification.message}
                    type={notification.type}
                    onClose={() =>
                      setNotification({ show: false, message: "", type: "" })
                    }
                  />
                )}

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="page-title-heading">
                    Subscriptions Management
                  </h4>
                  {/* <button
                    className="btn btn-info"
                    onClick={() =>
                      showNotification(
                        "This is a test notification!",
                        "success"
                      )
                    }
                  >
                    Test Notification
                  </button> */}
                </div>

                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden"></span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table dt-responsive nowrap">
                        <thead>
                          <tr>
                            <th>User Name</th>
                            <th>Email</th>
                            <th>Plan Type</th>
                            <th>Payment ID</th>
                            <th>Valid Through</th>
                            <th>Status</th>
                            <th>Features</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentSubs.length > 0 ? (
                            currentSubs.map((sub) => (
                              <tr key={sub._id}>
                                <td>{sub.userId?.name || "N/A"}</td>
                                <td>{sub.userId?.email || "N/A"}</td>
                                <td>{sub.planType}</td>
                                <td>{sub.paymentId?.payment_id || "N/A"}</td>
                                <td>{formatDate(sub.validThrough)}</td>
                                <td>
                                  <span
                                    className={`badge ${
                                      sub.isActive ? "bg-success" : "bg-warning"
                                    }`}
                                  >
                                    {sub.isActive ? "Active" : "Expired"}
                                  </span>
                                </td>
                                <td>
                                  <ul className="list-unstyled mb-0">
                                    {sub.planFeatures
                                      .filter(
                                        (feature) => feature.enabled === true
                                      )
                                      .map((feature) => (
                                        <li>{feature.feature.name}</li>
                                      ))}
                                  </ul>
                                </td>
                                <td>
                                  <div className="btn-group">
                                    <button
                                      onClick={() => handleEditClick(sub)}
                                      className="btn btn-primary btn-sm me-2 mr-2"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteClick(sub)}
                                      className="btn btn-danger btn-sm"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center">
                                No subscriptions found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div>
                        <select
                          className="form-select form-select-sm"
                          value={subsPerPage}
                          onChange={handleLimitChange}
                        >
                          <option value="5">5 per page</option>
                          <option value="10">10 per page</option>
                          <option value="25">25 per page</option>
                          <option value="50">50 per page</option>
                        </select>
                      </div>

                      <div>
                        <span className="me-3">
                          Page {currentPage} of {totalPages} (Total:{" "}
                          {subscriptions.length} records)
                        </span>

                        <div className="btn-group">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                          >
                            First
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Prev
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                          >
                            Last
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Subscription Modal */}
      {selectedSub && (
        <EditSubscriptionModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          subscription={selectedSub}
          onSubscriptionUpdated={onSubscriptionUpdated}
          showNotification={showNotification}
        />
      )}

      {/* Delete Confirmation Modal */}
      {selectedSub && (
        <DeleteConfirmationModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          title="Delete Subscription"
          message={`Are you sure you want to delete the subscription for user "${
            selectedSub.userId?.name || "Unknown"
          }"? This action cannot be undone.`}
        />
      )}
    </div>
  );
};

export default Subscription;
